/**
 * ClipRenderer — the seam the render path plugs into (implementation-plan.md §2.1).
 *
 * Primary adapter: WebCodecs + mediabunny (web/src/render/webcodecs.ts).
 * Fallback: `callout-render` / server ffmpeg (web/src/render/server.ts), selected at session
 * start from the capability probe (§2.3).
 * ffmpeg.wasm is NOT a path (plan §4.2 — GPL core, 0.04x native, iOS Safari crashes).
 */

export type RendererKind = 'webcodecs' | 'server';

/** Which route a clip's audio took. Degradation is reported, never hidden. */
export type AudioTrackMode = 'passthrough' | 'reencode' | 'mute';

/** What the browser can actually do — the result of the session-start probe (§2.5). */
export interface CapabilityReport {
  /** `full` = video + audio encode; `partial` = video only (Safari 16.4-18.7); `none` = no H.264. */
  webcodecs: 'full' | 'partial' | 'none';
  /** H.264 codec strings this browser can encode, best profile first (the §2.2 ladder). */
  videoEncode: string[];
  /** Audio codecs this browser can encode (e.g. `['aac']`); empty = video-only clips. */
  audioEncode: string[];
  /** The renderer the session should use. `server` when there is no usable H.264 encoder. */
  renderer: RendererKind;
  warnings: string[];
}

/** Branded overlay drawn onto every frame by the canvas compositor (branding/compositeFrame). */
export interface OverlayConfig {
  title: string;
  logoUrl?: string;
  primaryColor: string;
  secondaryColor: string;
  clubName?: string;
  /**
   * Free tier: draw the "Made with Callout" watermark in the bottom-right corner (card §2).
   * The flag comes from the account's subscription tier, never from the coach — on free tier the
   * watermark is not removable by the user.
   */
  watermark?: boolean;
}

export interface RenderRequest {
  /** Blob (local capture) or a URL (R2 / signed media URL). */
  sourceVideo: Blob | string;
  /** Seconds into the source. */
  tIn: number;
  /** Seconds into the source. */
  tOut: number;
  overlay: OverlayConfig;
  format: 'mp4';
  /** `preview` caps the output resolution; `export` keeps the source resolution. */
  quality: 'preview' | 'export';
  /**
   * `passthrough` copies the source audio packets (no re-encode — the Safari path);
   * `reencode` decodes and re-encodes to AAC-LC; `mute` drops audio entirely.
   * Omitted = decide from the capability report: AAC encode if available, else passthrough
   * for an AAC source, else video-only.
   */
  audioTrack?: AudioTrackMode;
}

export interface RenderResult {
  blob: Blob;
  /** Length of the rendered clip, in seconds. */
  duration: number;
  sizeBytes: number;
  renderTimeMs: number;
  renderer: RendererKind;
  /** What actually happened to the audio track in this render. */
  audioTrack: AudioTrackMode;
  /** The H.264 codec string the video was encoded with (ladder winner). */
  videoCodec: string | null;
  width: number;
  height: number;
}

/** Per-clip progress, frame-count based — what drives the queue's progress indicator. */
export interface RenderProgress {
  frames: number;
  /** Expected frames for the segment; null when the source rate could not be measured. */
  totalFrames: number | null;
}

export interface RenderOptions {
  onProgress?: (progress: RenderProgress) => void;
}

export interface ClipRenderer {
  readonly kind: RendererKind;
  /** Re-probes the environment. The UI shows the report; the selector acts on it. */
  probe(): Promise<CapabilityReport>;
  render(request: RenderRequest, options?: RenderOptions): Promise<RenderResult>;
}

/** The H.264 profile ladder, best first — probed in this order (§2.2). */
export const H264_LADDER = ['avc1.64001f', 'avc1.4d001f', 'avc1.42001f'] as const;

export const H264_PROFILE_NAMES: Record<string, string> = {
  'avc1.64001f': 'High',
  'avc1.4d001f': 'Main',
  'avc1.42001f': 'Baseline',
};

export const AAC_CODEC = 'mp4a.40.2';
